import React, { useCallback, useEffect, useState } from 'react';
import { getRecentActivity } from '../services/notificationApi';
import { Icon, PanelHeader } from './ui';

const ADMIN_ROLES = ['ADMIN', 'SUPER_ADMIN', 'SYSTEM_ADMIN'];

/* ── rows ─────────────────────────────────────────────────────────── */

const SkeletonRow = () => (
  <div className="ds-row">
    <div className="skeleton" style={{ width: 32, height: 32, borderRadius: 10, flexShrink: 0 }} />
    <div className="ds-row-main">
      <div className="skeleton skeleton-line" style={{ width: '70%', height: 12, borderRadius: 6, marginBottom: 6 }} />
      <div className="skeleton skeleton-line" style={{ width: '30%', height: 10, borderRadius: 6 }} />
    </div>
  </div>
);

const ActivityRow = ({ item }) => {
  const color = item.colorTag || '#64748b';
  return (
    <div className="ds-row" style={{ alignItems: 'flex-start' }}>
      {/* colorTag comes from the API as a hex colour; tint the icon box with it */}
      <span className="ds-icon-box ds-icon-box--sm" style={{ background: `${color}1f`, color }}>
        <Icon name="activity" size={15} strokeWidth={2.3} />
      </span>
      <div className="ds-row-main">
        <div
          style={{
            fontSize: 13, color: 'var(--ds-text)', lineHeight: 1.45,
            overflow: 'hidden', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}
        >
          {item.text}
        </div>
      </div>
      <span className="ds-row-time" style={{ paddingTop: 2 }}>{item.timeAgo}</span>
    </div>
  );
};

/* ── card ─────────────────────────────────────────────────────────── */

/**
 * Shared "Recent Activity" dashboard card — used on Patient, Doctor, and
 * Admin dashboards (one component, three data sources).
 *
 * Backend:
 *   GET /api/notifications/recent?limit=5   (patient / doctor)
 *   GET /api/admin/audit/recent?limit=10    (admin — 403 for other roles)
 *
 * `role` decides which endpoint gets called (see getRecentActivity in
 * notificationApi.js). Refetches on mount and on window focus since
 * `timeAgo` is computed server-side per request and goes stale if cached.
 *
 * Fixed height + internal scroll so it lines up with the other dashboard
 * cards regardless of how many items come back; width stays fluid so it
 * still slots into the same responsive flex rows as its siblings.
 */
const RecentActivityCard = ({ role, limit }) => {
  const isAdmin = ADMIN_ROLES.includes((role || '').toUpperCase());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    getRecentActivity(role, limit)
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [role, limit]);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  return (
    <div className="ds-panel ds-panel--fixed">
      <PanelHeader icon="clock" title="Recent Activity" />

      <div className="ds-panel-body">
        {loading && Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={i} />)}

        {!loading && error && (
          <div className="ds-empty">
            <span>
              Could not load recent activity.{' '}
              <button className="ds-retry" onClick={load}>Retry</button>
            </span>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="ds-empty">
            <span className="ds-icon-box ds-icon-box--lg tone-slate"><Icon name="clock" size={24} /></span>
            {isAdmin ? 'No activity across tenants yet' : 'No recent activity yet'}
          </div>
        )}

        {!loading && !error && items.length > 0 && items.map((item) => <ActivityRow key={item.id} item={item} />)}
      </div>
    </div>
  );
};

export default RecentActivityCard;